import DetailViewRoute from '@/views/recipe/detail/DetailViewRoute.vue';
import LoginView from '@/views/login/LoginView.vue';
import {
  type RouteLocationNormalized,
  type RouteLocationRaw,
  type RouteRecordRaw,
  type NavigationGuardNext,
  RouterView,
} from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';
import RecipeViewRoute from '@/views/recipe/RecipeViewRoute.vue';
import GroceriesViewRoute from '@/views/groceries/GroceriesViewRoute.vue';
import { getCurrentUser } from 'vuefire';
import { useAuthStore } from '@/stores/auth';
import { type User } from 'firebase/auth';
import ProfileViewRoute from '@/views/profile/ProfileViewRoute.vue';
import AuthenticatedRoute from '@/views/main/AuthenticatedRoute.vue';
import ShareViewRoute from '@/views/recipe/sharing/ShareViewRoute.vue';
import InviteViewRoute from '@/views/invite/InviteViewRoute.vue';
import WeekPlanViewRoute from '@/views/weekplan/WeekPlanViewRoute.vue';

type ScrollStore = {
  [key: string]: number;
};

type ScrollPosition = {
  behavior?: ScrollOptions['behavior'];
  left?: number;
  top?: number;
};

const saved: ScrollStore = {};

function saveScrollPosition(route: RouteLocationNormalized) {
  saved[route.fullPath] = document.documentElement.scrollTop;
}

function getScrollPosition(route: RouteLocationNormalized): ScrollPosition {
  if (saved[route.fullPath]) {
    return { top: saved[route.fullPath], behavior: 'smooth' };
  }
  return { top: 0, behavior: 'smooth' };
}

async function waitForCurrentUser(
  to: RouteLocationNormalized,
): Promise<RouteLocationRaw | void> {
  if (to.meta.requiresAuth) {
    const user: User | null | undefined = await getCurrentUser();
    if (user) {
      const authStore = useAuthStore();
      authStore.setUser(user);
    } else {
      return { path: '/login', query: { redirect: to.fullPath } };
    }
  }
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    meta: {
      requiresAuth: true,
    },
    component: AuthenticatedRoute,
    children: [
      {
        path: '/',
        name: 'recipe',
        component: RouterView,
        redirect: { name: 'list' },
        meta: { title: 'Recipes' },
        children: [
          {
            path: '/',
            name: 'list',
            component: RecipeViewRoute,
          },
          {
            path: '/:id',
            name: 'detail',
            component: DetailViewRoute,
            props(route: RouteLocationNormalized) {
              return { ...route.params };
            },
          },
          {
            path: '/share/:id',
            name: 'share',
            component: ShareViewRoute,
            props(route: RouteLocationNormalized) {
              return { ...route.params };
            },
          },
        ],
      },
      {
        path: '/invite/:id',
        name: 'invite',
        meta: { title: 'Invite' },
        component: InviteViewRoute,
        props(route: RouteLocationNormalized) {
          return { ...route.params };
        },
      },
      {
        path: '/profile',
        name: 'profile',
        meta: { title: 'Profile' },
        component: ProfileViewRoute,
      },
      {
        path: '/weekplan',
        name: 'weekplan',
        meta: { title: 'Week Plan' },
        component: WeekPlanViewRoute,
      },
      {
        path: '/groceries',
        name: 'groceries',
        meta: { title: 'Groceries' },
        component: GroceriesViewRoute,
      },
    ],
  },
  {
    path: '/login',
    name: 'login',
    meta: { title: 'Login' },
    component: LoginView,
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: (to: RouteLocationNormalized) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(getScrollPosition(to));
      }, 250);
    });
  },
  routes,
});

router.beforeEach(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    saveScrollPosition(from);
    const redirect = await waitForCurrentUser(to);
    if (redirect) {
      next(redirect);
      return;
    }
    next();
  },
);

export default router;
